import { useTranslation } from 'react-i18next'
import { Facebook, Linkedin, Youtube, Instagram, Twitter } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

const footerNavItems: RouteKey[] = [
  'home',
  'services',
  'history',
  'certifications',
  'contacts',
]

const legalNavItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'privacy', labelKey: 'footer.privacy' },
]

export function Footer() {
  const { t } = useTranslation('common')

  return (
    <footer className="bg-brand-dark text-white/80">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand Column */}
          <div>
            <LocalizedLink to="home" className="inline-block mb-4">
              <img
                src="/images/logos/chiggiato-logo-reverse.png"
                alt="Chiggiato SpA"
                className="h-12 object-contain"
              />
            </LocalizedLink>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.facebook.com/chiggiatospa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-white/60 hover:text-brand-red transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/chiggiato-spa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-white/60 hover:text-brand-red transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@chiggiatospa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-white/60 hover:text-brand-red transition-colors"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/chiggiatospa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-white/60 hover:text-brand-red transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/chiggiatospa"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X / Twitter"
                className="text-white/60 hover:text-brand-red transition-colors"
              >
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-tight mb-4">
              {t('footer.navigation')}
            </h3>
            <ul className="space-y-2">
              {footerNavItems.map((key) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="text-white/60 hover:text-white transition-colors text-sm"
                  >
                    {t(`nav.${key}`)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-white font-bold uppercase tracking-tight mb-4">
              {t('footer.contacts')}
            </h3>
            <address className="not-italic text-white/60 text-sm leading-relaxed space-y-2">
              <p>{t('contact.details.padova.address')}</p>
              <p>{t('contact.details.padova.city')}</p>
              <p>{t('contact.details.padova.phone')}</p>
              <p>{t('contact.details.padova.email')}</p>
            </address>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Chiggiato SpA. {t('footer.allRightsReserved')}
          </p>
          <div className="flex items-center gap-4">
            {legalNavItems.map(({ key, labelKey }) => (
              <LocalizedLink
                key={key}
                to={key}
                className="text-white/40 hover:text-white/60 transition-colors text-sm"
              >
                {t(labelKey)}
              </LocalizedLink>
            ))}
            <span className="text-white/40 text-sm">
              {t('footer.legalNotes')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  )
}
